import { useCallback } from 'react';
import { useFormHook, useSetError } from '@Hooks/index';
import { useSetRegisteredAddress } from '@Hooks/api/useSetRegisteredAddress';
import { useNavigationManager } from '@Router/useNavigationManager';
import { useBoundStore } from '@Store/index';
import { OnboardingFormFields, USA_STATES_LIST } from '@Types/index';
import { getStateDropdownItem } from '@Components/inputs/AddressStateDropDown';
import { RoutesMap } from '@Router/router.types';
import { useValidateAutoComplete } from '@Hooks/providers/useValidateAutoComplete';
import { RegisteredAddressSchema } from '../registeredAddressForm/registeredAddressSchema';
import { syncRegisteredAddress } from './syncRegisteredAddress';
import type { IRegisteredAddressForm } from '../registeredAddressForm/registeredAddressSchema';
import type { ExtendedUseFormReturn } from '@Hooks/index';

export const useRegisteredAddressLogic = () => {
  const {
    customer: {
      customerData: { registeredBusinessAddressData, customerAddressData },
    },
    getIsUsFlow,
  } = useBoundStore((state) => state);

  const isHomeAddressUS = getIsUsFlow();
  const { line1, line2, postalCode, state, city } = registeredBusinessAddressData ?? {};

  const usStates = USA_STATES_LIST;
  const isHomeStateSupported = usStates.some(
    (stateItem) => stateItem.value === customerAddressData?.state
  );

  const defaultValues: IRegisteredAddressForm = {
    [OnboardingFormFields.LINE1]: line1 || '',
    [OnboardingFormFields.APARTMENT]: line2 || '',
    [OnboardingFormFields.CITY]: city ?? '',
    [OnboardingFormFields.STATE]: getStateDropdownItem(state, null, usStates) ?? {
      title: '',
      value: '',
    },
    [OnboardingFormFields.ZIP]: postalCode ?? '',
    [OnboardingFormFields.REGISTERED_ADDRESS_SAME_AS_PRIMARY]: false,
  };

  const { navigateToNextPage } = useNavigationManager();

  const formMethods: ExtendedUseFormReturn<IRegisteredAddressForm> = useFormHook({
    formSchema: RegisteredAddressSchema,
    formDefaultValues: defaultValues,
    schemaTitle: 'registeredAddressSchema',
    mode: 'onTouched',
  });

  const { mutateAsync: asyncSetRegisteredAddress, isPending } = useSetRegisteredAddress(
    (_, input) => {
      syncRegisteredAddress(input);
      navigateToNextPage(RoutesMap.REGISTERED_ADDRESS);
    }
  );

  const handleSubmit = useCallback(async () => {
    const formData = formMethods.getValues();
    asyncSetRegisteredAddress(formData as IRegisteredAddressForm);
    syncRegisteredAddress(formData as IRegisteredAddressForm);
  }, [formMethods, asyncSetRegisteredAddress]);

  useSetError({ formMethods });
  useValidateAutoComplete({
    formMethods,
    relevantFields: [
      OnboardingFormFields.CITY,
      OnboardingFormFields.STATE,
      OnboardingFormFields.ZIP,
      OnboardingFormFields.LINE1,
      OnboardingFormFields.APARTMENT,
    ],
  });

  return {
    handleSubmit,
    formMethods,
    isPending,
    defaultValues,
    customerAddressData,
    isHomeAddressUS,
    isHomeStateSupported,
    usStates,
  };
};
